import { useState } from "react";
import { ImageTextSection } from "./ImageTextSection";

export interface TabItem {
  title: string;
  subtitle?: string;
  image: string;
  imagePosition?: "left" | "right";
  variant?: "split" | "background";
  paragraphs: string[];
  bullets: string[];
  buttonLabel?: string;
  listTitle?: string;
  postParagraphs?: string[];
}

export interface TabsSectionProps {
  tabs: Record<string, TabItem>;
  defaultTabKey?: string;
}

export function TabsSection({ tabs, defaultTabKey }: TabsSectionProps) {
  const keys = Object.keys(tabs);
  const [activeTab, setActiveTab] = useState<string>(defaultTabKey || keys[0] || "");
  const currentTab = tabs[activeTab];

  if (!currentTab) return null;

  return (
    <section className="bg-black pt-16 lg:pt-12 text-white">
      <div className="container-bizu">
        <div className="flex flex-wrap justify-center border-b border-white/10">
          {keys.map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative px-6 py-4 text-xs font-bold uppercase tracking-wider transition-all duration-300 md:text-sm cursor-pointer ${
                activeTab === key ? "text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              {tabs[key].buttonLabel || tabs[key].title}

              {activeTab === key && (
                <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-accent" />
              )}
            </button>
          ))}
        </div>
      </div>

      <ImageTextSection
        key={activeTab}
        title={currentTab.title}
        image={currentTab.image}
        imagePosition={currentTab.imagePosition}
        variant={currentTab.variant}
        description={
          <>
            {currentTab.subtitle && (
              <span className="mb-6 block text-xs font-bold uppercase tracking-[0.25em] text-accent">
                {currentTab.subtitle}
              </span>
            )}

            {currentTab.paragraphs.map((paragraph) => (
              <p key={paragraph} className="mt-5 first:mt-0">
                {paragraph}
              </p>
            ))}

            {currentTab.bullets && currentTab.bullets.length > 0 && (
              <div className="mt-8 space-y-4">
                <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-white/50">
                  {currentTab.listTitle || "Applications"}
                </h4>

                <ul className="space-y-3">
                  {currentTab.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center">
                      <span className="mr-3 select-none text-lg font-bold leading-none text-accent">
                        &gt;
                      </span>

                      <span className="text-base font-light leading-relaxed text-white/80 md:text-lg">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {currentTab.postParagraphs && currentTab.postParagraphs.map((paragraph) => (
              <p key={paragraph} className="mt-5 text-white/90">
                {paragraph}
              </p>
            ))}
          </>
        }
      />
    </section>
  );
}
